import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { Printer, RotateCw, Download } from 'lucide-react';

const SITE_URL = 'https://laytonpace.com';

export const DESIGNS = [
  {
    id: 'monogram',
    name: 'Monogram',
    tagline: 'Better in person.',
    back: 'Scan. Then let\'s talk.',
  },
  {
    id: 'wordmark',
    name: 'Wordmark',
    tagline: 'Tall. Employed. Actually reads books.',
    back: 'The rest is on the site.',
  },
  {
    id: 'minimal',
    name: 'Minimal',
    tagline: 'Not on the apps. Just here.',
    back: 'Scan me.',
  },
  {
    id: 'handwritten',
    name: 'Handwritten',
    tagline: 'Hi.',
    back: 'scan, if you\'re curious',
  },
];

const CardFront = ({ design }) => {
  const isPaper = design.id === 'handwritten';
  return (
    <div
      data-testid={`card-front-${design.id}`}
      className="card-face card-front relative overflow-hidden"
      style={{
        width: '3.5in',
        height: '2in',
        background: isPaper ? '#f3ede0' : '#0a0a0a',
        color: isPaper ? '#1a1a1a' : '#f5f5f0',
      }}
    >
      {/* subtle grain / paper texture */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: isPaper ? 0.22 : 0.08,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {design.id === 'monogram' && (
        <div className="h-full w-full flex flex-col justify-between p-[0.28in]">
          <div className="flex items-start justify-between">
            <div
              className="border border-white/70 flex items-center justify-center"
              style={{
                width: '0.55in',
                height: '0.55in',
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: '0.28in',
                letterSpacing: '0.02in',
              }}
            >
              LP
            </div>
            <div
              className="text-right uppercase"
              style={{
                fontSize: '7pt',
                letterSpacing: '0.15em',
                color: '#a8a8a8',
              }}
            >
              Est. — in real life
            </div>
          </div>

          <div>
            <div
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: '22pt',
                lineHeight: '1',
                fontWeight: 400,
                letterSpacing: '-0.01em',
              }}
            >
              Layton Pace
            </div>
            <div
              className="mt-[0.06in]"
              style={{
                fontSize: '9pt',
                color: '#c9c9c9',
                fontStyle: 'italic',
              }}
            >
              {design.tagline}
            </div>
          </div>
        </div>
      )}

      {design.id === 'wordmark' && (
        <div className="h-full w-full flex flex-col justify-between p-[0.28in]">
          <div
            className="uppercase"
            style={{
              fontSize: '7pt',
              letterSpacing: '0.3em',
              color: '#a8a8a8',
            }}
          >
            — Hello.
          </div>

          <div>
            <div
              className="uppercase"
              style={{
                fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                fontSize: '30pt',
                lineHeight: '0.95',
                fontWeight: 800,
                letterSpacing: '-0.02em',
              }}
            >
              LAYTON
              <br />
              PACE
            </div>
          </div>

          <div
            className="border-t border-white/30 pt-[0.08in]"
            style={{ fontSize: '8pt', color: '#c9c9c9' }}
          >
            {design.tagline}
          </div>
        </div>
      )}

      {design.id === 'minimal' && (
        <div className="h-full w-full flex items-center justify-center p-[0.28in]">
          <div className="text-center">
            <div
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: '10pt',
                color: '#a8a8a8',
                letterSpacing: '0.3em',
              }}
              className="uppercase"
            >
              L · P
            </div>
            <div
              className="mt-[0.08in]"
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: '26pt',
                lineHeight: '1',
              }}
            >
              Layton Pace
            </div>
            <div
              className="mt-[0.1in]"
              style={{
                fontSize: '8.5pt',
                color: '#c9c9c9',
                fontStyle: 'italic',
              }}
            >
              {design.tagline}
            </div>
          </div>
        </div>
      )}

      {design.id === 'handwritten' && (
        <div className="h-full w-full flex flex-col justify-between p-[0.3in]">
          <div
            className="uppercase"
            style={{
              fontSize: '6.5pt',
              letterSpacing: '0.35em',
              color: '#6b6357',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}
          >
            — a note
          </div>

          <div style={{ marginTop: '-0.08in' }}>
            <div
              style={{
                fontFamily: '"Caveat", "Homemade Apple", cursive',
                fontSize: '44pt',
                lineHeight: '0.95',
                color: '#121212',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              Hi,
            </div>
            <div
              style={{
                fontFamily: '"Caveat", cursive',
                fontSize: '22pt',
                lineHeight: '1',
                color: '#2a2a2a',
                fontWeight: 500,
                marginTop: '0.02in',
              }}
            >
              I&apos;m Layton.
            </div>
          </div>

          <div className="flex items-end justify-between">
            <div
              style={{
                fontFamily: '"Caveat", cursive',
                fontSize: '11pt',
                color: '#4a4438',
              }}
            >
              turn me over →
            </div>
            <div
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: '7pt',
                letterSpacing: '0.25em',
                color: '#6b6357',
              }}
              className="uppercase"
            >
              L · P
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CardBack = ({ design }) => {
  const isPaper = design.id === 'handwritten';
  return (
    <div
      data-testid={`card-back-${design.id}`}
      className="card-face card-back relative overflow-hidden"
      style={{
        width: '3.5in',
        height: '2in',
        background: isPaper ? '#f3ede0' : '#f5f5f0',
        color: '#0a0a0a',
      }}
    >
      {isPaper && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.22,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
      )}
      <div className="h-full w-full flex items-center p-[0.25in] gap-[0.2in] relative">
        <div
          className="flex items-center justify-center bg-white"
          style={{
            width: '1.5in',
            height: '1.5in',
            padding: '0.06in',
            border: '1px solid #0a0a0a',
          }}
        >
          <QRCodeCanvas
            value={SITE_URL}
            size={256}
            level="H"
            includeMargin={false}
            style={{ width: '100%', height: '100%' }}
            fgColor="#0a0a0a"
            bgColor="#ffffff"
          />
        </div>

        <div className="flex-1">
          <div
            className="uppercase"
            style={{
              fontSize: '7pt',
              letterSpacing: '0.3em',
              color: '#5a5a5a',
            }}
          >
            {isPaper ? '— p.s.' : '— Scan'}
          </div>
          <div
            className="mt-[0.06in]"
            style={{
              fontFamily: isPaper
                ? '"Caveat", cursive'
                : 'Georgia, "Times New Roman", serif',
              fontSize: isPaper ? '19pt' : '13pt',
              lineHeight: '1.1',
              fontWeight: isPaper ? 600 : 400,
            }}
          >
            {design.back}
          </div>
          <div
            className="mt-[0.14in] pt-[0.08in] border-t border-black/30"
            style={{ fontSize: '9pt', letterSpacing: '0.05em' }}
          >
            laytonpace.com
          </div>
        </div>
      </div>
    </div>
  );
};

export default function BusinessCard() {
  const [designIdx, setDesignIdx] = useState(0);
  const [side, setSide] = useState('front');
  const design = DESIGNS[designIdx];

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      className="min-h-screen bg-neutral-100 pt-28 pb-20 px-6"
      data-testid="business-card-page"
    >
      {/* Controls — hidden on print */}
      <div className="max-w-5xl mx-auto mb-10 print:hidden">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="uppercase text-xs tracking-[0.3em] text-neutral-500">
              Private · Not linked
            </div>
            <h1
              className="mt-2 text-4xl sm:text-5xl text-neutral-900"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              Business Card
            </h1>
            <p className="mt-2 text-sm text-neutral-600 max-w-md">
              Standard US size (3.5&quot; × 2&quot;). Three designs — front and
              back of each. Download the ZIP for print-ready 576 DPI PNGs
              (upload to MOO / VistaPrint), or print the selected design
              directly from your browser.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="/downloads/laytonpace-cards.zip"
              download
              data-testid="download-zip-btn"
              className="inline-flex items-center gap-2 bg-white border border-neutral-900 text-neutral-900 px-5 py-3 text-sm uppercase tracking-[0.2em] hover:bg-neutral-900 hover:text-white transition-colors"
            >
              <Download className="w-4 h-4" />
              Download ZIP
            </a>
            <button
              onClick={handlePrint}
              data-testid="print-card-btn"
              className="inline-flex items-center gap-2 bg-neutral-900 text-white px-5 py-3 text-sm uppercase tracking-[0.2em] hover:bg-black transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print / Save PDF
            </button>
          </div>
        </div>

        {/* Design picker */}
        <div className="mt-10 flex flex-wrap gap-2">
          {DESIGNS.map((d, i) => (
            <button
              key={d.id}
              onClick={() => setDesignIdx(i)}
              data-testid={`design-option-${d.id}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                i === designIdx
                  ? 'bg-neutral-900 text-white border-neutral-900'
                  : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900'
              }`}
            >
              {d.name}
            </button>
          ))}
          <button
            onClick={() => setSide(side === 'front' ? 'back' : 'front')}
            data-testid="flip-card-btn"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border border-neutral-300 bg-white text-neutral-700 hover:border-neutral-900"
          >
            <RotateCw className="w-3.5 h-3.5" />
            Show {side === 'front' ? 'Back' : 'Front'}
          </button>
        </div>
      </div>

      {/* Live preview — interactive, one side at a time */}
      <div className="max-w-5xl mx-auto flex justify-center print:hidden">
        <div
          className="shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]"
          style={{ transform: 'scale(1.6)', transformOrigin: 'top center' }}
        >
          {side === 'front' ? (
            <CardFront design={design} />
          ) : (
            <CardBack design={design} />
          )}
        </div>
      </div>

      {/* Spacing beneath scaled preview */}
      <div className="h-[3.5in] print:hidden" />

      {/* Print layout — front + back stacked, real size */}
      <div className="print-only hidden">
        <div className="print-card">
          <CardFront design={design} />
        </div>
        <div className="print-card">
          <CardBack design={design} />
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: 3.5in 2in;
            margin: 0;
          }
          html, body {
            background: #fff !important;
          }
          .print\\:hidden { display: none !important; }
          .print-only { display: block !important; }
          .print-card {
            page-break-after: always;
            break-after: page;
          }
          .print-card:last-child {
            page-break-after: auto;
            break-after: auto;
          }
        }
      `}</style>
    </div>
  );
}

// Capture route — renders a single card at exact size with no chrome,
// used by the screenshot script to export high-res PNGs into the ZIP.
export function CardCapture() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const designId = params.get('design') || 'monogram';
  const sideParam = params.get('side') || 'front';
  const design = DESIGNS.find((d) => d.id === designId) || DESIGNS[0];

  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.background = '#ffffff';
    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.background = '';
    };
  }, []);

  return (
    <div
      data-testid="card-capture-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        background: '#ffffff',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
      }}
    >
      {sideParam === 'back' ? (
        <CardBack design={design} />
      ) : (
        <CardFront design={design} />
      )}
    </div>
  );
}
